import {
  put,
  takeLatest,
  call,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import _ from 'lodash';
import queryString from 'query-string';
import { Base64 } from 'js-base64';
import {
  REQUEST_OTP,
  REQUEST_OTP_COOLDOWN_EXPIRE,
  VERIFY_OTP,
  VERIFY_SSN,
  FETCH_PROFILE,
  FETCH_GOV_ID_RESPONSE,
  REQUEST_OTP_HIDE_ANIMATION,
  GET_GOV_ID_LINK,
  GET_GOV_ID_LINK_SUCCESS,
  GET_GOV_ID_LINK_FAILURE,
  GET_PREFILL_HINT,
  GET_PREFILL_HINT_SUCCESS,
  GET_PREFILL_HINT_FAILURE,
} from '../constants';
import {
  verifyOtpSuccess,
  verifyOtpFailure,
  requestOtpSuccess,
  requestOtpFailure,
  verifySsnSuccess,
  verifySsnFailure,
  fetchProfileFailure,
  fetchProfileSuccess,
  saveUserProfile,
  registerUserFailure,
  getGovIdLinkSuccess,
  getGovIdLinkFailure,
  getPrefillHintSuccess,
  getPrefillHintFailure,
} from '../actions';
import { fetchUser } from './../../services/auth';

const createIdvSagas = (api) => {
  function* fetchGovIdResponse(action) {
    const {
      code,
      push,
    } = action.payload;
    const queryParams = queryString.parse(window.location.search);
    const stateFromIdw = _.get(queryParams, 'state', false);
    let callbackUri;
    let govIdType;
    let accessToken = false;
    let clientId = false;
    if (stateFromIdw) {
      let decodedStateQueryParamObj;
      try {
        const decodedStateQueryParam = Base64.decode(decodeURIComponent(stateFromIdw));
        decodedStateQueryParamObj = JSON.parse(decodedStateQueryParam);
        callbackUri = _.get(decodedStateQueryParamObj, 'callbackUri', false);
        govIdType = _.get(decodedStateQueryParamObj, 'govIdType', false);
        accessToken = _.get(decodedStateQueryParamObj, 'accessToken', false);
        clientId = _.get(decodedStateQueryParamObj, 'clientId', false);
      } catch (error) {
        push('/idv/failed');
        return;
      }
    }

    try {
      // Initial call to exchange code for transaction
      const initialResponse = yield call(api.fetchGovIdResponse, {
        code,
        govIdType,
        accessToken,
      });
      const initialStatusCode = _.get(initialResponse, 'status', false);
      // Also check for nested statusCode like other sagas do
      const nestedStatusCode = _.get(initialResponse, 'data.data.statusCode', false);
      const duplicatedEmail = _.get(initialResponse, 'data.data.duplicatedEmail', '');

      // Check both HTTP status and nested statusCode (like other sagas)
      if (initialStatusCode === 200 || nestedStatusCode === 200) {
        // Check if we have a transaction_id for polling
        // Try multiple possible response paths - check all possible locations
        const transactionId = _.get(initialResponse, 'data.data.transaction_id', false) ||
                              _.get(initialResponse, 'data.transaction_id', false) ||
                              _.get(initialResponse, 'data.data.transactionId', false) ||
                              _.get(initialResponse, 'data.transactionId', false) ||
                              _.get(initialResponse, 'transaction_id', false) ||
                              _.get(initialResponse, 'transactionId', false);

        const policyDecision = _.get(initialResponse, 'data.data.policyDecision', false) ||
                               _.get(initialResponse, 'data.policyDecision', false);
        const idvResponse = _.get(initialResponse, 'data.data.pass', false) ||
                           _.get(initialResponse, 'data.pass', false);

        // Check if pass is truthy (could be boolean true, string "true", or 1)
        const isApproved = idvResponse === true || idvResponse === 'true' || idvResponse === 1 ||
                          policyDecision === 'approve' || policyDecision === 'approved';

        // Check if status is explicitly pending
        const isPending = policyDecision === 'pending' ||
                        (transactionId && !isApproved && !duplicatedEmail);

        if (duplicatedEmail) {
          push(`/user-duplicated?clientId=${clientId}`);
          yield put(registerUserFailure(initialStatusCode, '', duplicatedEmail));
          return;
        }

        // If we have a transaction_id, always poll (even if initial response looks approved)
        // This ensures we get the final status from IDDW
        // Also check for truthy value (not just existence) to handle empty strings, null, etc.
        if (transactionId && transactionId !== false && transactionId !== null && transactionId !== '') {
          const maxPollingAttempts = 60; // 5 minutes max (60 * 5 seconds)
          const pollingInterval = 5000; // 5 seconds
          let pollingAttempts = 0;
          let finalResponse = initialResponse;

          // Poll until we get a final status or max attempts reached
          while (pollingAttempts < maxPollingAttempts) {
            yield delay(pollingInterval);
            pollingAttempts += 1;

            try {
              const statusResponse = yield call(api.checkGovIdStatus, {
                transactionId,
                govIdType,
                accessToken,
              });

              const statusCode = _.get(statusResponse, 'status', false);
              const nestedStatusCode = _.get(statusResponse, 'data.data.statusCode', false);

              // Check both HTTP status and nested statusCode
              if (statusCode === 200 || nestedStatusCode === 200) {
                // Try multiple possible response paths
                const currentPolicyDecision = _.get(statusResponse, 'data.data.policyDecision', false) ||
                                             _.get(statusResponse, 'data.policyDecision', false);
                const currentPass = _.get(statusResponse, 'data.data.pass', false) ||
                                   _.get(statusResponse, 'data.pass', false);
                const currentDuplicatedEmail = _.get(statusResponse, 'data.data.duplicatedEmail', '') ||
                                              _.get(statusResponse, 'data.duplicatedEmail', '');
                const errorCode = _.get(statusResponse, 'data.data.errorCode', '') ||
                                 _.get(statusResponse, 'data.errorCode', '');
                const errorDescription = _.get(statusResponse, 'data.data.errorDescription', '') ||
                                        _.get(statusResponse, 'data.errorDescription', '');

                // Check if pass is truthy (could be boolean true, string "true", or 1)
                const isApproved = currentPass === true || currentPass === 'true' || currentPass === 1 ||
                                  currentPolicyDecision === 'approve' || currentPolicyDecision === 'approved';

                // Check if we have a final status
                if (isApproved) {
                  finalResponse = statusResponse;
                  break;
                }

                // Check for denial or error
                if (currentPolicyDecision === 'deny' || currentPolicyDecision === 'denied' || errorCode) {
                  finalResponse = statusResponse;
                  break;
                }

                // If duplicated email detected
                if (currentDuplicatedEmail) {
                  push(`/user-duplicated?clientId=${clientId}`);
                  yield put(registerUserFailure(statusCode, '', currentDuplicatedEmail));
                  return;
                }

                // Continue polling if status is still pending
                finalResponse = statusResponse;
              } else {
                // If status check fails, break and use last known response
                break;
              }
            } catch (pollError) {
              // If polling fails, break and use last known response
              break;
            }
          }

          // Process final response - try multiple possible response paths
          const finalPolicyDecision = _.get(finalResponse, 'data.data.policyDecision', false) ||
                                     _.get(finalResponse, 'data.policyDecision', false);
          const finalPass = _.get(finalResponse, 'data.data.pass', false) ||
                           _.get(finalResponse, 'data.pass', false);
          const finalDuplicatedEmail = _.get(finalResponse, 'data.data.duplicatedEmail', '') ||
                                      _.get(finalResponse, 'data.duplicatedEmail', '');
          const finalNestedStatusCode = _.get(finalResponse, 'data.data.statusCode', false);

          // Check if pass is truthy (could be boolean true, string "true", or 1)
          // Also check if nested statusCode is 200 AND pass is true (like other sagas)
          const isFinalApproved = (finalPass === true || finalPass === 'true' || finalPass === 1) ||
                                 finalPolicyDecision === 'approve' ||
                                 finalPolicyDecision === 'approved' ||
                                 (finalNestedStatusCode === 200 && finalPass);

          if (isFinalApproved) {
            const user = fetchUser();
            if (callbackUri) {
              const redirect = `${callbackUri}?accessToken=${accessToken || user.accessToken}&refreshToken=${user.refreshToken}&identityToken=${user.identityToken}&expiresIn=${user.expiresIn}`;
              window.location.replace(redirect);
            } else {
              // Even without callbackUri, we should handle successful verification
              const defaultCallbackUri = '/update-profile';
              window.location.replace(`${defaultCallbackUri}?accessToken=${user.accessToken}&refreshToken=${user.refreshToken}&identityToken=${user.identityToken}&expiresIn=${user.expiresIn}`);
            }
          } else if (finalDuplicatedEmail) {
            push(`/user-duplicated?clientId=${clientId}`);
            yield put(registerUserFailure(initialStatusCode, '', finalDuplicatedEmail));
          } else {
            push(`/idv/failed${callbackUri ? `?callbackUri=${callbackUri}` : ''}`);
          }
        } else {
          // No transaction_id, process initial response as final
          // Re-check for approval in case the response structure was different
          const noTransactionPolicyDecision = _.get(initialResponse, 'data.data.policyDecision', false) ||
                                             _.get(initialResponse, 'data.policyDecision', false);
          const noTransactionPass = _.get(initialResponse, 'data.data.pass', false) ||
                                   _.get(initialResponse, 'data.pass', false);
          const noTransactionNestedStatusCode = _.get(initialResponse, 'data.data.statusCode', false);

          // Check multiple approval conditions
          const isNoTransactionApproved = (noTransactionPass === true || noTransactionPass === 'true' ||
                                         noTransactionPass === 1) ||
                                         noTransactionPolicyDecision === 'approve' ||
                                         noTransactionPolicyDecision === 'approved' ||
                                         (noTransactionNestedStatusCode === 200 && noTransactionPass);

          if (isNoTransactionApproved) {
            const user = fetchUser();
            if (callbackUri) {
              const redirect = `${callbackUri}?accessToken=${accessToken || user.accessToken}&refreshToken=${user.refreshToken}&identityToken=${user.identityToken}&expiresIn=${user.expiresIn}`;
              window.location.replace(redirect);
            } else {
              const defaultCallbackUri = '/update-profile';
              window.location.replace(`${defaultCallbackUri}?accessToken=${user.accessToken}&refreshToken=${user.refreshToken}&identityToken=${user.identityToken}&expiresIn=${user.expiresIn}`);
            }
          } else {
            push(`/idv/failed${callbackUri ? `?callbackUri=${callbackUri}` : ''}`);
          }
        }
      } else {
        push(`/idv/failed${callbackUri ? `?callbackUri=${callbackUri}` : ''}`);
      }
    } catch (e) {
      push(`/idv/failed${callbackUri ? `?callbackUri=${callbackUri}` : ''}`);
    }
  }

  function* watchFetchGovIdResponse() {
    yield takeLatest(FETCH_GOV_ID_RESPONSE, fetchGovIdResponse);
  }

  function* requestOtpSaga(action) {
    const {
      type,
      showOtpRequestAnimation,
    } = action.payload;
    try {
      const response = yield call(api.requestOtp, { type });
      const statusCode = _.get(response, 'status', false);
      if (statusCode === 200) {
        const asi = _.get(response, 'data.data.asi', null);
        yield put(requestOtpSuccess({ asi }));
      } else {
        const errorDetail = _.get(response, 'data.errors.0.message', false);
        yield put(requestOtpFailure({
          statusCode,
          errorDetail,
        }));
      }

      if (showOtpRequestAnimation) {
        yield delay(5000);
        yield put({ type: REQUEST_OTP_HIDE_ANIMATION });
        yield delay(25000);
        yield put({ type: REQUEST_OTP_COOLDOWN_EXPIRE });
      } else {
        yield delay(30000);
        yield put({ type: REQUEST_OTP_COOLDOWN_EXPIRE });
      }
    } catch (e) {
      const errorDetail = _.get(e.response, 'data.data.message', false);
      yield put(requestOtpFailure({ errorDetail }));
    }
  }


  function* watchRequestOtp() {
    yield takeLatest(REQUEST_OTP, requestOtpSaga);
  }

  function* verifyOtpSaga(action) {
    const {
      asi,
      pin,
      type,
      push,
      onVerifyFailed,
    } = action.payload;
    const queryParams = queryString.parse(window.location.search);
    const callbackUri = _.get(queryParams, 'callbackUri', false);
    const clientId = _.get(queryParams, 'clientId', false);
    const callbackUriStr = callbackUri ? `?callbackUri=${callbackUri}&clientId=${clientId}` : '';
    try {
      const response = yield call(api.verifyOtp, {
        asi,
        pin,
        type,
      });
      const statusCode = _.get(response, 'data.data.statusCode', false);
      const hardFail = _.get(response, 'data.data.hardFail', false);
      const user = fetchUser();
      if (statusCode === 200) {
        const userData = yield call(api.fetchProfile);
        const userDataParsed = _.get(userData, 'data.data.attributes', false);

        if (hardFail) {
          push(`/idv/failed${callbackUriStr}`);
          yield put(verifyOtpSuccess({ response }));
        } else if (userDataParsed['custom:gov_id_verification'] === '-' && userDataParsed['custom:verified'] === 'N') {
          push(`/idv/select-gov-id-type${callbackUriStr}`);
          yield put(verifyOtpSuccess({ response }));
        } else if (userDataParsed['custom:gov_id_verification'] === 'X' && userDataParsed['custom:verified'] === 'N' && userDataParsed['custom:ssn_verification'] === 'N' && userDataParsed['custom:phone_verification'] === 'N') {
          if (onVerifyFailed()) {
            push(`/idv/failed${callbackUriStr}`);
          }
        } else if (
          userDataParsed['custom:verified'] === 'Y' ||
          (userDataParsed['custom:verified'] === 'N' && userDataParsed['custom:ssn_verification'] === 'Y' && userDataParsed['custom:phone_verification'] === 'Y') ||
          (userDataParsed['custom:verified'] === 'N' && userDataParsed['custom:ssn_verification'] === 'N' && userDataParsed['custom:gov_id_verification'] === 'Y' && userDataParsed['custom:phone_verification'] === 'Y')
        ) {
          const defaultCallbackUri = '/update-profile';
          window.location.replace(`${callbackUri || defaultCallbackUri}?accessToken=${user.accessToken}&refreshToken=${user.refreshToken}&identityToken=${user.identityToken}&expiresIn=${user.expiresIn}`);
        } else {
          push(`/idv/failed${callbackUriStr}`);
          yield put(verifyOtpSuccess({ response }));
        }
      } else {
        const errorDetail = _.get(response, 'data.errors.0.message', false);
        if (onVerifyFailed()) {
          push(`/idv/ssn4${callbackUri ? callbackUriStr : ''}`);
        }
        yield put(verifyOtpFailure({
          statusCode,
          errorDetail,
        }));
      }
    } catch (e) {
      const errorDetail = _.get(e.response, 'data.data.message', false);
      if (onVerifyFailed()) {
        push(`/idv/ssn4${callbackUriStr}`);
      }
      yield put(verifyOtpFailure({ errorDetail }));
    }
  }


  function* watchVerifyOtp() {
    yield takeLatest(VERIFY_OTP, verifyOtpSaga);
  }

  function* verifySsnSaga(action) {
    const {
      ssn,
      push,
    } = action.payload;

    try {
      const response = yield call(api.verifySsn, { ssn });
      const statusCode = _.get(response, 'data.data.statusCode', false);
      if (statusCode === 200) {
        yield put(verifySsnSuccess());
        const queryParams = queryString.parse(window.location.search);
        const callbackUri = _.get(queryParams, 'callbackUri', false);
        const user = fetchUser();
        const userData = yield call(api.fetchProfile);
        const userDataParsed = _.get(userData, 'data.data.attributes', false);
        if (userDataParsed['custom:ssn_verification'] === 'Y') { // custom:verified
          if (callbackUri) {
            window.location.replace(`${callbackUri}?accessToken=${user.accessToken}&refreshToken=${user.refreshToken}&identityToken=${user.identityToken}&expiresIn=${user.expiresIn}`);
          }
        } else {
          push('/idv/failed');
        }
        const errorDetail = _.get(response, 'data.errors.0.message', false);
        yield put(verifySsnFailure({
          statusCode,
          errorDetail,
        }));
      }
    } catch (e) {
      const errorDetail = _.get(e.response, 'data.data.message', false);
      yield put(verifySsnFailure({ errorDetail }));
    }
  }


  function* watchVerifySsn() {
    yield takeLatest(VERIFY_SSN, verifySsnSaga);
  }

  function* fetchProfileSaga(action) {
    const { isUpdateProfile } = action.payload;
    try {
      const queryParams = queryString.parse(window.location.search);
      let accessToken = _.get(queryParams, 'accessToken', false);
      if (!accessToken) {
        const user = fetchUser();
        ({ accessToken } = user);
      }
      const response = yield call(api.fetchProfile, accessToken);
      const userDataParsed = _.get(response, 'data.data.attributes', false);
      yield put(saveUserProfile({ userData: userDataParsed }));
      const statusCode = _.get(response, 'data.data.statusCode', false);
      if (statusCode === 200) {
        const profile = _.get(response, 'data.data.attributes', null);
        yield put(fetchProfileSuccess(profile, isUpdateProfile));
      } else {
        const errorDetail = _.get(response, 'data.data.message', false);
        yield put(fetchProfileFailure({ errorDetail }));
      }
    } catch (e) {
      const errorDetail = _.get(e.response, 'data.data.message', false);
      yield put(fetchProfileFailure({ errorDetail }));
    }
  }

  function* watchFetchProfile() {
    yield takeLatest(FETCH_PROFILE, fetchProfileSaga);
  }

  function* getGovIdLinkSaga(action) {
    const {
      govIdType,
      linkType,
    } = action.payload;

    try {
      // Get the GovID link via backend
      // Backend will handle getting the IDDW token internally
      const response = yield call(api.getGovIdLink, {
        govIdType,
        linkType: linkType || 'browser', // Default to 'browser' for desktop flow
      });
      const statusCode = _.get(response, 'status', false);
      const nestedStatusCode = _.get(response, 'data.data.statusCode', false);

      if (statusCode === 200 || nestedStatusCode === 200) {
        // Try multiple possible paths for the link
        const link = _.get(response, 'data.data.link', false) ||
                    _.get(response, 'data.data.url', false) ||
                    _.get(response, 'data.link', false) ||
                    _.get(response, 'data.url', false) ||
                    _.get(response, 'data.body.link', false) ||
                    _.get(response, 'data.body.url', false) ||
                    _.get(response, 'link', false) ||
                    _.get(response, 'url', false);

        if (link) {
          // Dispatch success action with the link instead of redirecting
          yield put(getGovIdLinkSuccess({ link }));
        } else {
          yield put(getGovIdLinkFailure('No link found in response'));
        }
      } else {
        const errorDetail = _.get(response, 'data.errors.0.message', false) ||
                           _.get(response, 'data.data.message', false) ||
                           _.get(response, 'data.body.message', false) ||
                           _.get(response, 'data.message', false);
        yield put(getGovIdLinkFailure(errorDetail || 'Unknown error'));
      }
    } catch (e) {
      const errorDetail = _.get(e.response, 'data.data.message', false) ||
                         _.get(e.response, 'data.errors.0.message', false);
      yield put(getGovIdLinkFailure(errorDetail || e.message || 'Unknown error'));
    }
  }

  function* watchGetGovIdLink() {
    yield takeLatest(GET_GOV_ID_LINK, getGovIdLinkSaga);
  }

  function* getPrefillHintSaga(action) {
    const {
      baseUrl,
      path,
      redirectUri,
      iddwClientId,
      encodedStateQueryParam,
    } = action.payload;

    try {
      const response = yield call(api.getPrefillHint);
      const statusCode = _.get(response, 'status', false);
      const nestedStatusCode = _.get(response, 'data.data.statusCode', false);

      if (statusCode === 200 || nestedStatusCode === 200) {
        const loginHint = _.get(response, 'data.data.login_hint', false) ||
                         _.get(response, 'data.login_hint', false) ||
                         _.get(response, 'data.body.login_hint', false) ||
                         _.get(response, 'login_hint', false);

        if (loginHint) {
          const encodedLoginHint = encodeURIComponent(loginHint);
          const redirectUrl = `${baseUrl}/${path}?client_id=${iddwClientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=openid+country.US&response_type=code&state=${encodedStateQueryParam}&login_hint=${encodedLoginHint}`;
          yield put(getPrefillHintSuccess({ redirectUrl }));
        } else {
          yield put(getPrefillHintFailure('No login_hint in response'));
        }
      } else {
        const errorDetail = _.get(response, 'data.errors.0.message', false) ||
                           _.get(response, 'data.data.message', false) ||
                           _.get(response, 'data.body.message', false) ||
                           _.get(response, 'data.message', false);
        yield put(getPrefillHintFailure(errorDetail || 'Unknown error'));
      }
    } catch (e) {
      const errorDetail = _.get(e.response, 'data.data.message', false) ||
                         _.get(e.response, 'data.errors.0.message', false);
      yield put(getPrefillHintFailure(errorDetail || e.message || 'Unknown error'));
    }
  }

  function* watchGetPrefillHint() {
    yield takeLatest(GET_PREFILL_HINT, getPrefillHintSaga);
  }

  return {
    watchRequestOtp,
    watchVerifyOtp,
    watchVerifySsn,
    watchFetchProfile,
    watchFetchGovIdResponse,
    watchGetGovIdLink,
    watchGetPrefillHint,
  };
};

export default createIdvSagas;
